import { useDisplayCurrency } from './currency/DisplayCurrencyProvider';
import { displayPresets, displayDefault, displayFixed, usdOf, boundMessageAmount, formatAmountLabel, remapOnCurrencyChange } from './Util/formatUtils';
import React, { useEffect, useState, useRef } from 'react';
import FormUnavailable from './Components/FormUnavailable';
import { usePostHog } from 'posthog-js/react';
import { AnimatedError, JOURNEY_MARKS, LEGACY_UNBRANDED_FORM_COLOR, Sentry, markJourney, safePostMessageToParent, safePostMessageWithSelfFallback, setFlowContext } from '@charitystack/shared';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ensureLostCheckoutTapListener } from './Util/checkoutAckWatcher';
import CustomAmountInput from './Util/CustomAmountInput.jsx';
import {
  attachDonateSnapshot,
  attachCartDisplayMetadata,
  isScheduledDonation,
  normalizeDisplayGift,
  snapshotOf,
  stripConfigOnlyKeys,
  usdFromDisplay,
  usdStringFromDisplay,
} from './currency/withDisplaySnapshot';
import FundsDropdown from './Util/FundsDropdown.jsx';
import StandardAmountButtons from './Util/StandardAmountButtons.jsx';
import CustomInputsPage from './CustomInputs.jsx';
import {
  useBrandColors,
  prepareColorsForPostMessage,
  calculateMaxTransactionAmount,
} from '@charitystack/shared';
import { fieldMapper } from './Util/FieldMapper.jsx';
import ScheduledDonationsSheet from './Util/ScheduledDonationsSheet.jsx';
import { usePaneSheet } from './Components/PaneSheet';
import { logApiError, isDevelopment } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
import { useDrawerHeightTransition } from './hooks/useDrawerHeightTransition';
import { FORM_TITLE_STRIP } from './Util/formDisplayUtils';
import { formatWithCommas } from './Util/formatUtils';
import { navigateAfterReset } from './Util/formNavigation';
import { scheduleFormReset } from './Util/formResetUtils';

import {
  clearScheduledDonationState,
  getScheduledDonationValidationErrors,
  resetCustomInputsAnswers,
} from './Util/formStateUtils';
import {
  applyScheduledDonationPayloadToData,
  attachCartSubmissionMetadata,
  buildCartItemFromSubmission,
  normalizeAmountArraysToStrings,
  normalizeRecurringAmountConfig,
} from './Util/formSubmissionUtils';
import {
  getStandardizedFormBodyStyles,
  getStandardizedFormElementStyles,
  getStandardizedFormHeight,
} from './Util/formHeightUtils';
import FormActionButtons from './Components/FormActionButtons';
import FormFrequencySection from './Components/FormFrequencySection';
import FormPageShell from './Components/FormPageShell';
import FormStartPageShell from './Components/FormStartPageShell';
import { getDetailPageBack } from './Util/detailPageBack';
import { MonthlyBumpPage } from './Util/MonthlyBumpPage';
import {
  applyMonthlyBumpMetadataToDonationData,
  buildMonthlyBumpAcceptedMetadata,
  calculateMonthlyBumpAmount,
} from './Util/monthlyBumpUtils';
import {
  getPreCheckoutStepPrimaryButtonLabel,
  getPrimaryLabelAfterCustomInputsStep,
  getFormCheckoutButtonLabel,
  getCtaFrequency,
} from './Util/formPrimaryCtaUtils';
import { ANALYTICS_CURRENCY, toMinorUnits } from './Util/analyticsProperties';
// PostHog form analytics
import { trackForm, FORM_EVENTS, FORM_TYPES, registerFormContext } from '@charitystack/shared';

dayjs.extend(isSameOrAfter);

const freqMap = {
  '': '',
  ONE_TIME: 'One-Time',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  ANNUALLY: 'Yearly',
};

const FIXED_AMOUNT_ELEMENT_IDS = ['dd254e67-71ea-4201-bb54-7690bc675d26'];

function StandardForm(props) {
  // Extract setPage prop for parent navigation (back button)
  const {
    setPage: setParentPage,
    transitionToPage,
    currentPage: parentCurrentPage,
    hostedPagePreview = false,
    enableEmbedForm = false,
    previewMode = false,
    urlOverrides = {},
    formDisplayMode = 'dynamic',
    hideFAQ = false,
    onSelectionChange,
  } = props;
  const isHostedPage = useIsHostedPage();
  const posthog = usePostHog();
  const ctx = useDisplayCurrency();

  const [singleFrequency, setSingleFrequency] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hostedPage, setHostedPage] = useState(null);
  const [elementID, setElementID] = useState('');
  const [configData, setConfigData] = useState({});
  const [showScheduledDonations, setShowScheduledDonations] = useState(false);
  const [maxTransactionAmount, setMaxTransactionAmount] = useState(1000);
  const [defaultPaymentProcessor, setDefaultPaymentProcessor] = useState('');

  const [customInputsValues, setCustomInputsValues] = useState([]);
  const [funds, setFunds] = useState(['General Fund', 'Zakat Fund', 'Sadaqah Fund']);
  const [frequencies, setFrequencies] = useState([
    'ONE_TIME',
    'DAILY',
    'WEEKLY',
    'MONTHLY',
    'ANNUALLY',
  ]);
  const [oneTimeAmounts, setOneTimeAmounts] = useState(['10', '25', '50', '100']);
  const [dailyAmounts, setDailyAmounts] = useState(['10', '25', '50', '100']);
  const [weeklyAmounts, setWeeklyAmounts] = useState(['10', '25', '50', '100']);
  const [monthlyAmounts, setMonthlyAmounts] = useState(['10', '25', '50', '100']);
  const [annuallyAmounts, setAnnuallyAmounts] = useState(['10', '25', '50', '100']);
  const [defaultOneTimeAmount, setDefaultOneTimeAmount] = useState('10');
  const [defaultDailyAmount, setDefaultDailyAmount] = useState('25');
  const [defaultWeeklyAmount, setDefaultWeeklyAmount] = useState('50');
  const [defaultMonthlyAmount, setDefaultMonthlyAmount] = useState('100');
  const [defaultAnnuallyAmount, setDefaultAnnuallyAmount] = useState('10');

  const [page, setPage] = useState('START_PAGE');
  const [pageDirection, setPageDirection] = useState(null);

  const fixedFormElementHeightForPageMotion = getStandardizedFormHeight({ formDisplayMode });
  const innerPageHeightTransitionEnabled =
    formDisplayMode === 'dynamic' && !props.isDrawerOpen && !fixedFormElementHeightForPageMotion;
  const { containerRef: standardFormPageContainerRef, prepareTransition: prepareInnerPageHeightTransition } =
    useDrawerHeightTransition(innerPageHeightTransitionEnabled);

  // Page changes are immediate; shared CSS handles visibility.
  const smoothSetPage = (targetPage, direction = null) => {
    prepareInnerPageHeightTransition();
    setPageDirection(direction);
    setPage(targetPage);
  };

  // Enhanced page transition function for callback-style setPage
  const smoothSetPageCallback = (callback, direction = null) => {
    const targetPage = callback(page);
    const resolvedDirection = targetPage === page ? null : direction;
    if (targetPage !== page) {
      prepareInnerPageHeightTransition();
    }
    if (transitionToPage && typeof transitionToPage === 'function') {
      setPageDirection(resolvedDirection);
      transitionToPage(targetPage, setPage, page);
    } else {
      setPageDirection(resolvedDirection);
      setPage(targetPage);
    }
  };
  const [defaultAmounts, setDefaultAmounts] = useState(oneTimeAmounts);

  const [frequency, setFrequency] = useState('');
  const [amount, setAmount] = useState('20');
  const [selectedFund, setSelectedFund] = useState('');
  // The frequency the frequency switching effect last resolved `amount` for. Until it catches
  // up with `frequency`, `amount` may still be the previous frequency's (or the '20'
  // placeholder above), so the selection is not reported (CON-336).
  const [amountFrequency, setAmountFrequency] = useState('');
  // Read by the reset paths, whose listener is registered once at mount (CON-336).
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const [isInitialized, setIsInitialized] = useState(false); // Track if form has been initialized
  const [isButtonSelection, setIsButtonSelection] = useState(true); // true when a preset button amount is active (custom input stays empty)

  const [errorMessage, setErrorMessage] = useState('');
  const [errorDisplayTime, setErrorDisplayTime] = useState(0);
  const [errorMessageInterval, setErrorMessageInterval] = useState(null);
  const [addToCart, setAddToCart] = useState(false);

  // Individual error states
  const [frequencyError, setFrequencyError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [fundError, setFundError] = useState('');
  const [scheduledDonationsError, setScheduledDonationsError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [customInputsResetKey, setCustomInputsResetKey] = useState(0);

  const [domID, setDomID] = useState('');
  // cardMin: per-merchant minimum donation amount from prodFeesTable (default $1).
  // Used for all amount validation — form submission, URL overrides, and custom amount input.
  // Falls back to $1 if API doesn't return it.
  const [cardMin, setCardMin] = useState(1.0);
  const [resetTimeoutId, setResetTimeoutId] = useState(null);
  // A11-003: the reset the last DONATE scheduled, so EXIT_CHECKOUT_FORM can run
  // that same reset early (see exitCheckoutListener).
  const pendingFormResetRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // NEW: Use shared brand colors hook
  // NOTE: element_config call has primaryColor defined as lightColor, therefore we take configData?.lightColor from the payload
  // CRITICAL: Use props.data?.lightColor as primary source since configData may be stale
  // CON-528: no colour is the no-brand set (the legacy "no colour" value), never sage.
  const colorForBrandColors =
    props.data?.lightColor || configData?.lightColor || LEGACY_UNBRANDED_FORM_COLOR;

  // CRITICAL FIX: Use pre-generated brand colors from parent (EmbedForm) if available
  // This prevents flicker caused by useBrandColors initializing with empty state
  const {
    brandColors: localBrandColors,
    primaryColor: localPrimaryColor,
    darkColor: localDarkColor,
  } = useBrandColors(colorForBrandColors);

  // Use parent's pre-generated colors if available, otherwise fall back to local generation
  const hasParentBrandColors =
    props.generatedBrandColors && Object.keys(props.generatedBrandColors).length > 0;
  const brandColors = hasParentBrandColors ? props.generatedBrandColors : localBrandColors;
  const primaryColor = hasParentBrandColors
    ? props.generatedBrandColors['accent-50'] || localPrimaryColor
    : localPrimaryColor;
  const darkColor = hasParentBrandColors
    ? props.generatedBrandColors['accent-60'] || localDarkColor
    : localDarkColor;

  // Scheduled donations states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeValue, setTimeValue] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [donationType, setDonationType] = useState('');
  const [scheduledDonationsOpen, setScheduledDonationsOpen] = useState(false);
  // The schedule editor opens as a sheet over the form card (ScheduledDonationsSheet).
  const scheduleSheet = usePaneSheet({ inline: true });

  const customInputsRef = useRef(null);
  // Track if user has manually changed frequency (to prevent unwanted resets in preview mode)
  const hasUserChangedFrequencyRef = useRef(false);
  // Track the previous defaultFrequency value to detect changes
  const previousDefaultFrequencyRef = useRef(null);
  // Guard to ensure URL overrides are only applied once on initial load
  const hasAppliedUrlOverridesRef = useRef(false);
  // Whether an applied prefill (URL params, element prefills, a carried selection) still holds
  // the amount, frequency, fund and answers against the config-driven defaults. Set when the
  // prefill applies; released by a reset or by a configuration whose selection fields changed,
  // after which the form re-derives from the configuration exactly as it does without a
  // prefill. The prefill itself is never re-applied (CON-336).
  const prefillHoldsStateRef = useRef(false);
  // The selection fields of the configuration this mount last applied (see the data effect).
  const selectionConfigKeyRef = useRef(null);
  // Tracks the frequency value that should skip the amount reset (set by URL override effect).
  // When the frequency switching effect runs with this exact frequency, it skips setAmount.
  // Cleared after one successful skip at the target frequency.
  const skipAmountResetForFrequencyRef = useRef(null);
  const skipAmountResetOnceRef = useRef(false);
  const urlAmountUsdRef = useRef(null);
  // The display figure a carried pick was made in, while it waits for this copy's display
  // currency to match it (see `carriedDisplayFor` and the report effect below).
  const carriedDisplayRef = useRef(null);
  const prevCtxRef = useRef(ctx);
  const presetSourceIndicesRef = useRef({});

  // A reset, or a changed configuration, puts the form back on the configured defaults: the
  // prefill stops holding, the amount reset runs again, and the reported pick is withdrawn
  // (CON-336).
  // A pinned URL amount is part of that prefill: once released, the amount on screen is a
  // configured default in the display currency, so the USD pin must not outlive it (DONATE
  // would post the old URL amount against the new display gift).
  const resettleSelection = () => {
    prefillHoldsStateRef.current = false;
    skipAmountResetForFrequencyRef.current = null;
    urlAmountUsdRef.current = null;
    carriedDisplayRef.current = null;
    onSelectionChangeRef.current?.(null);
  };

  // CON-336 x CON-316: under conversion the other copy reports its pick as a USD `amount`
  // (the unit every amount prefill is read in) plus `display`, the figure the donor actually
  // saw. When this copy shows the same currency at the same rate, it opens on that exact
  // figure instead of the USD amount's re-conversion (which can drift by a minor unit), and
  // keeps the USD pin only when the other copy had one (a URL amount the donor left alone).
  const carriedDisplayFor = (usdAmount) => {
    const display = props.carriedSelection?.display;
    if (!display || props.carriedSelection.amount !== usdAmount) return null;
    if (typeof display.amount !== 'string' || display.amount === '') return null;
    return { ...display, usd: usdAmount };
  };
  const carriedDisplayMatches = (carried, targetCtx) =>
    Boolean(carried) &&
    targetCtx.converted === true &&
    carried.currency === targetCtx.displayCurrency &&
    carried.fxRate === targetCtx.fxRate;

  // Wrapper function to mark manual frequency changes in preview mode
  const handleFrequencyChange = (newFrequency) => {
    // Track frequency change
    // CRITICAL: Wrap in try-catch to ensure analytics never blocks user flow
    if (newFrequency !== frequency) {
      try {
        trackForm(FORM_EVENTS.FREQUENCY_CHANGED, {
          frequency: newFrequency,
          previous_frequency: frequency,
          form_id: configData?.formID,
          form_type: FORM_TYPES.STANDARD,
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }
    }

    if (previewMode) {
      hasUserChangedFrequencyRef.current = true;
    }
    // Clear URL override amount protection — user is now manually choosing a frequency,
    // so the form should show default amounts for the new frequency, not the URL override.
    skipAmountResetForFrequencyRef.current = null;
    urlAmountUsdRef.current = null;
    setFrequency(newFrequency);
  };

  // Wrapper function to mark manual frequency changes in preview mode
  const handleSelectedFrequencyChange = (newFrequency) => {
    if (previewMode) {
      hasUserChangedFrequencyRef.current = true;
    }
    setSelectedFrequency(newFrequency);
  };

  // Color generation now handled by useBrandColors hook

  // Track previous drawer state to detect close
  const prevIsDrawerOpenRef = useRef(props.isDrawerOpen);
  // Ref to track if preset data has triggered auto-open (prevents repeated auto-opens)
  // Declared here so it can be used in drawer close cleanup below
  const hasInitialPresetDataRef = useRef(false);

  useEffect(() => {
    setIsDrawerOpen(props.isDrawerOpen);

    // Detect drawer close (transition from true → false)
    // Only collapse the picker UI and reset auto-open tracking
    // IMPORTANT: Don't clear date values - user may want to retry with same selections
    // The dates are preserved so handleDonate() can still read them on subsequent attempts
    if (prevIsDrawerOpenRef.current === true && props.isDrawerOpen === false) {
      setScheduledDonationsOpen(false);
      hasInitialPresetDataRef.current = false;
    }

    // Update ref for next comparison
    prevIsDrawerOpenRef.current = props.isDrawerOpen;
  }, [props.isDrawerOpen]);

  // Cleanup timeout on unmount to prevent React warnings
  useEffect(() => {
    return () => {
      if (resetTimeoutId) {
        clearTimeout(resetTimeoutId);
      }
    };
  }, [resetTimeoutId]);

  // NOTE: Scheduled donations picker is always collapsed by default
  // User must manually click "Add Start/End Date & Time" to expand it
  // hasInitialPresetDataRef is kept for drawer close cleanup logic

  useEffect(() => {
    // Skip if URL overrides have already been applied — they take priority over
    // config-driven defaults. Without this guard, a configData re-reference
    // (from the data init effect) triggers this effect and overwrites the
    // URL-specified amount with the form's default amount.
    if (prefillHoldsStateRef.current) return;

    if (donationType === 'Once') {
      setAmount(defaultOneTimeAmount === '0' ? '' : defaultOneTimeAmount);
      setDefaultAmounts(oneTimeAmounts);
    } else if (donationType === 'Repeat') {
      const defaultFreq = configData.defaultFrequency || '';
      if (defaultFreq === 'DAILY') {
        setAmount(defaultDailyAmount === '0' ? '' : defaultDailyAmount);
        setDefaultAmounts(dailyAmounts);
      } else if (defaultFreq === 'WEEKLY') {
        setAmount(defaultWeeklyAmount === '0' ? '' : defaultWeeklyAmount);
        setDefaultAmounts(weeklyAmounts);
      } else if (defaultFreq === 'MONTHLY') {
        setAmount(defaultMonthlyAmount === '0' ? '' : defaultMonthlyAmount);
        setDefaultAmounts(monthlyAmounts);
      } else if (defaultFreq === 'ANNUALLY') {
        setAmount(defaultAnnuallyAmount === '0' ? '' : defaultAnnuallyAmount);
        setDefaultAmounts(annuallyAmounts);
      }
    }
  }, [configData]);

  const resetFormState = () => {
    setIsSubmitting(false);
    resettleSelection();
    // Match the existing RESET_ELEMENT behavior
    smoothSetPage('START_PAGE');
    setCustomInputsResetKey((prevKey) => prevKey + 1);

    // Reset to INITIAL values from configData, not empty values
    const defaultFreq = configData.defaultFrequency || '';
    setFrequency(defaultFreq);

    // Set the appropriate default amount based on the default frequency
    // Also determine if the resolved amount is a button selection or custom entry
    const resolveResetAmount = (defaultAmt, buttonAmounts) => {
      const hasDefault = defaultAmt && defaultAmt !== '0' && defaultAmt !== '';
      if (hasDefault) return { value: defaultAmt, isButton: buttonAmounts.includes(defaultAmt) };
      const firstButton = buttonAmounts?.find((a) => a && a !== '');
      return { value: firstButton || '', isButton: true };
    };

    if (defaultFreq === 'ONE_TIME') {
      const { value, isButton } = resolveResetAmount(defaultOneTimeAmount, oneTimeAmounts);
      setAmount(value);
      setIsButtonSelection(isButton);
      setDonationType('Once');
    } else if (defaultFreq === 'DAILY') {
      const { value, isButton } = resolveResetAmount(defaultDailyAmount, dailyAmounts);
      setAmount(value);
      setIsButtonSelection(isButton);
      setDonationType('Repeat');
    } else if (defaultFreq === 'WEEKLY') {
      const { value, isButton } = resolveResetAmount(defaultWeeklyAmount, weeklyAmounts);
      setAmount(value);
      setIsButtonSelection(isButton);
      setDonationType('Repeat');
    } else if (defaultFreq === 'MONTHLY') {
      const { value, isButton } = resolveResetAmount(defaultMonthlyAmount, monthlyAmounts);
      setAmount(value);
      setIsButtonSelection(isButton);
      setDonationType('Repeat');
    } else if (defaultFreq === 'ANNUALLY') {
      const { value, isButton } = resolveResetAmount(defaultAnnuallyAmount, annuallyAmounts);
      setAmount(value);
      setIsButtonSelection(isButton);
      setDonationType('Repeat');
    } else {
      setAmount('');
      setIsButtonSelection(true);
      setDonationType('Once');
    }

    setSelectedFrequency(defaultFreq);

    // Reset the fund the way the mount does: the first fund is the default,
    // however many funds there are. Restoring it only for a single-fund form
    // left every multi-fund form blank after the post-DONATE reset, so a donor
    // who closed the checkout came back to an empty Select Fund and a Next that
    // refused to move. Found on the CON-16 device walk.
    setSelectedFund(funds.length >= 1 ? funds[0] : '');

    // Always clear scheduled donation state to prevent stale values
    // from persisting across form submissions (even if picker was closed)
    // FIX: Previously this was conditional on scheduledDonationsOpen, but the
    // reset runs 2.5s after clicking "Support" - by then the user may have
    // closed the drawer, leaving stale date values that break subsequent submissions
    clearScheduledDonationState({
      setStartDate,
      setEndDate,
      setTimeValue,
      setScheduledDonationsOpen,
    });

    // Reset the preset data tracking ref to allow fresh auto-open behavior
    hasInitialPresetDataRef.current = false;

    // Clear any errors
    setFrequencyError('');
    setAmountError('');
    setFundError('');
    setScheduledDonationsError('');
    setStartDateError('');
    setEndDateError('');
    setTimeError('');
    setErrorMessage('');

    // Reset custom inputs to empty answers
    if (configData.customInputs) {
      setCustomInputsValues(resetCustomInputsAnswers(configData.customInputs));
    }

    // Navigate to correct parent page using utility
    navigateAfterReset({
      setParentPage,
      isHostedPage,
      enableEmbedForm,
      formDisplayMode,
      isDrawerOpen: props.isDrawerOpen,
    });
  };

  const listener = (event) => {
    if (!(event.data.entity == 'CHARITYSTACK' && event.data.action == 'RESET_ELEMENT')) return;

    setIsSubmitting(false);
    resettleSelection();
    smoothSetPage('START_PAGE');
    setCustomInputsResetKey((prevKey) => prevKey + 1);
    setFrequency('');
    setAmount('');
    setSelectedFund('');

    props.fetchElementsConfig({ forceApply: true }).catch((err) => console.error('ERROR', err));
  };

  // A11-003: the donor closed checkout, so finish the pending post-DONATE reset
  // now instead of when its 2.5s timer fires. It re-enables Support before the
  // hosted page returns focus to it; a close inside that window used to land on
  // another control because Support was still disabled. Runs the SAME reset the
  // timer would (captured at submit), so only the timing changes. Every other
  // path, including error recovery, keeps the timer.
  const exitCheckoutListener = (event) => {
    if (!(event.data?.entity === 'CHARITYSTACK' && event.data?.action === 'EXIT_CHECKOUT_FORM')) return;
    if (event.data?.data?.errorRecovery) return;
    const pending = pendingFormResetRef.current;
    if (!pending) return;
    pendingFormResetRef.current = null;
    clearTimeout(pending.timeoutId);
    pending.run();
  };

  const goBackListener = (event) => {
    if (!(event.data.entity == 'CHARITYSTACK' && event.data.action == 'GO_BACK')) return;

    setIsSubmitting(false);
    smoothSetPageCallback(
      (currentPage) => {
        if (
          currentPage === 'CUSTOM_INPUTS_PAGE' &&
          customInputsRef.current?.goToPreviousFieldPage?.()
        ) {
          return 'CUSTOM_INPUTS_PAGE';
        }

        if (currentPage === 'START_PAGE') {
          return 'START_PAGE';
        }
        return 'START_PAGE';
      },
      'back',
    );
  };

  useEffect(() => {
    if (hostedPage !== null) {
      // send message that element has finished loading
      safePostMessageToParent(
        {
          action: 'FINISHED_LOADING',
          entity: 'CHARITYSTACK',
          sender: 'STANDARD_FORM',
          receiver: 'HOSTED_PAGE',
        },
        '*',
      );
    }
  }, [hostedPage]);

  useEffect(() => {
    window.addEventListener('message', listener);
    window.addEventListener('message', goBackListener);
    window.addEventListener('message', exitCheckoutListener);

    // grab hostedPage boolean
    const urlParams = new URLSearchParams(window.location.search);
    const hostedPage = urlParams.get('page');
    // hostedPage: null (embedded) or truthy value (hosted page)
    setHostedPage(hostedPage);
    setDomID(urlParams.get('domID'));
    return () => {
      window.removeEventListener('message', listener);
      window.removeEventListener('message', goBackListener);
      window.removeEventListener('message', exitCheckoutListener);
    };
  }, []);

  useEffect(() => {
    const { data } = props;

    // Reset frequency tracking when new data arrives (form initialization)
    // Only reset if this is truly a new form (elementID changed)
    if (data && data.elementID !== elementID) {
      hasUserChangedFrequencyRef.current = false;
      previousDefaultFrequencyRef.current = null;
    }

    // preprocess data
    data.customInputs.forEach((input) => {
      input.answers = [''];
    });
    // convert amounts to strings
    // Tolerates missing/null/object amounts values; a malformed config must
    // degrade to no preset buttons, never throw and blank the whole form.
    normalizeAmountArraysToStrings(data);
    const toStr = (v) => (v != null && v !== '' ? String(v) : '');
    data.defaultOneTimeAmount = toStr(data.defaultOneTimeAmount);
    data.defaultDailyAmount = toStr(data.defaultDailyAmount);
    data.defaultWeeklyAmount = toStr(data.defaultWeeklyAmount);
    data.defaultMonthlyAmount = toStr(data.defaultMonthlyAmount);
    data.defaultAnnuallyAmount = toStr(data.defaultAnnuallyAmount);

    // A configuration whose selection fields changed (a refresh that differs, or the dashboard
    // editing the form) ends any prefill's hold, so the form re-derives from it below instead
    // of keeping an amount or fund the new configuration may not show (CON-336). Compared by
    // content: EmbedForm hands a new `data` object on every render for some forms.
    const selectionConfigKey = JSON.stringify([
      data.funds,
      data.frequencies,
      data.defaultFrequency,
      data.oneTimeAmounts,
      data.dailyAmounts,
      data.weeklyAmounts,
      data.monthlyAmounts,
      data.annuallyAmounts,
      data.defaultOneTimeAmount,
      data.defaultDailyAmount,
      data.defaultWeeklyAmount,
      data.defaultMonthlyAmount,
      data.defaultAnnuallyAmount,
      data.cardMin,
      data.maxTransactionAmount,
      data.maxTransactionAmountIsProcessorLimit,
      data.defaultPaymentProcessor,
    ]);
    if (selectionConfigKeyRef.current !== null && selectionConfigKeyRef.current !== selectionConfigKey) {
      resettleSelection();
    }
    selectionConfigKeyRef.current = selectionConfigKey;

    // Handle showScheduleDonation with proper default (false if undefined)
    const shouldShowScheduledDonations = data.showScheduleDonation ?? false;

    setShowScheduledDonations(shouldShowScheduledDonations);

    // If showScheduledDonations is being turned OFF, clear the scheduled data and close the picker
    // Note: When enabled, ScheduledDonations component handles frequency-aware config lookup
    if (!shouldShowScheduledDonations) {
      clearScheduledDonationState({
        setStartDate,
        setEndDate,
        setTimeValue,
        setScheduledDonationsOpen,
      });
    }
    setCardMin(data.cardMin ?? 1.0);
    setElementID(data.elementID);

    // Set payment processor and calculate appropriate max amount
    const processor = data.defaultPaymentProcessor || '';
    setDefaultPaymentProcessor(processor);

    // Calculate max transaction amount based on payment processor
    const calculatedMaxAmount = calculateMaxTransactionAmount(
      processor,
      data.maxTransactionAmount,
      data.maxTransactionAmountIsProcessorLimit,
    );
    setMaxTransactionAmount(calculatedMaxAmount);

    // Only reset custom inputs and fund to defaults if URL overrides haven't been applied.
    // Without this guard, React 18 strict mode (or any props.data re-reference) re-runs
    // this effect and overwrites the URL-specified fund/custom input values.
    if (!prefillHoldsStateRef.current) {
      setCustomInputsValues(data.customInputs);
    }
    setFunds(data.funds);
    if (data.funds.length >= 1 && !prefillHoldsStateRef.current) {
      setSelectedFund(data.funds[0]);
    }
    setFrequencies(data.frequencies);

    // Detect if defaultFrequency setting has changed
    const defaultFrequencyChanged =
      previousDefaultFrequencyRef.current !== null &&
      previousDefaultFrequencyRef.current !== data.defaultFrequency;

    // Update the previous value
    previousDefaultFrequencyRef.current = data.defaultFrequency;

    // Determine if we should auto-set the frequency
    // In preview mode, only auto-set on:
    // 1. Initial load (hasUserChangedFrequencyRef.current === false)
    // 2. When defaultFrequency setting changes (defaultFrequencyChanged === true)
    const shouldAutoSetFrequency =
      !previewMode || !hasUserChangedFrequencyRef.current || defaultFrequencyChanged;

    if (shouldAutoSetFrequency && !prefillHoldsStateRef.current) {
      // Update frequency based on defaultFrequency
      // Skip if URL overrides have already applied a frequency — re-running this would
      // overwrite the URL-specified frequency and trigger a cascade that resets the amount.
      setFrequency(data.defaultFrequency);

      // CRITICAL: Set donationType based on defaultFrequency for preview mode
      // This ensures the UI correctly shows "Once" vs "Repeat" toggle
      if (data.defaultFrequency === 'ONE_TIME') {
        setDonationType('Once');
      } else if (data.defaultFrequency && data.defaultFrequency !== '') {
        setDonationType('Repeat');
      }

      // If defaultFrequency changed, reset the tracking flag so future manual changes work
      if (defaultFrequencyChanged && previewMode) {
        hasUserChangedFrequencyRef.current = false;
      }
    }

    if (!isInitialized) {
      setIsInitialized(true);
    }

    if (data.frequencies.length == 0 || data.frequencies.length == 1) {
      setSingleFrequency(freqMap[data.defaultFrequency] || data.defaultFrequency);
    }
    const bounds = { minUsd: data.cardMin ?? 1.0, maxUsd: calculatedMaxAmount };
    for (const [freq, key] of Object.entries({ ONE_TIME: 'oneTime', DAILY: 'daily', WEEKLY: 'weekly', MONTHLY: 'monthly', ANNUALLY: 'annually' })) {
      presetSourceIndicesRef.current[freq] = data[`${key}Amounts`].map((_, index) => index);
    }
    setOneTimeAmounts(displayPresets(data.oneTimeAmounts, ctx, bounds));
    setDailyAmounts(displayPresets(data.dailyAmounts, ctx, bounds));
    setWeeklyAmounts(displayPresets(data.weeklyAmounts, ctx, bounds));
    setMonthlyAmounts(displayPresets(data.monthlyAmounts, ctx, bounds));
    setAnnuallyAmounts(displayPresets(data.annuallyAmounts, ctx, bounds));
    // Effective values already computed above (new ?? legacy) and normalized on data
    const effectiveOneTime = data.defaultOneTimeAmount || '';
    const effectiveDaily = data.defaultDailyAmount || '';
    const effectiveWeekly = data.defaultWeeklyAmount || '';
    const effectiveMonthly = data.defaultMonthlyAmount || '';
    const effectiveAnnually = data.defaultAnnuallyAmount || '';
    setDefaultOneTimeAmount(displayDefault(effectiveOneTime, data.oneTimeAmounts, ctx, bounds));
    setDefaultDailyAmount(displayDefault(effectiveDaily, data.dailyAmounts, ctx, bounds));
    setDefaultWeeklyAmount(displayDefault(effectiveWeekly, data.weeklyAmounts, ctx, bounds));
    setDefaultMonthlyAmount(displayDefault(effectiveMonthly, data.monthlyAmounts, ctx, bounds));
    setDefaultAnnuallyAmount(displayDefault(effectiveAnnually, data.annuallyAmounts, ctx, bounds));

    // Set initial defaultAmounts based on the default frequency in preview mode
    // if (!isInitialized) {
    // }
    if (data.defaultFrequency === 'ONE_TIME') {
      setDefaultAmounts(displayPresets(data.oneTimeAmounts, ctx, bounds));
    } else if (data.defaultFrequency === 'DAILY') {
      setDefaultAmounts(displayPresets(data.dailyAmounts, ctx, bounds));
    } else if (data.defaultFrequency === 'WEEKLY') {
      setDefaultAmounts(displayPresets(data.weeklyAmounts, ctx, bounds));
    } else if (data.defaultFrequency === 'MONTHLY') {
      setDefaultAmounts(displayPresets(data.monthlyAmounts, ctx, bounds));
    } else if (data.defaultFrequency === 'ANNUALLY') {
      setDefaultAmounts(displayPresets(data.annuallyAmounts, ctx, bounds));
    }

    // Color setting now handled by useBrandColors hook automatically

    setConfigData(data);
    // In hosted page context, never allow Add to Cart (hosted pages don't have cart bar)
    // Always show "Support" button instead
    const shouldShowAddToCart = isHostedPage ? false : (data.showAddToCart ?? null);
    setAddToCart(shouldShowAddToCart);

    safePostMessageWithSelfFallback(window.top,
      {
        action: 'IFRAME_LOADED',
        entity: 'CHARITYSTACK',
        sender: 'STANDARD_FORM',
        receiver: 'PARENT_WEBSITE',
        data: JSON.stringify(data),
        id: data.sourceWebsite,
      },
      '*',
    );
    // FCD-944: Changed dependency from [props] to [props.data] to ensure preview updates when data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  // Track form:viewed when configData is loaded
  const hasTrackedFormView = useRef(false);
  useEffect(() => {
    if (configData?.formID && !hasTrackedFormView.current) {
      hasTrackedFormView.current = true;

      // Register form context as super properties
      registerFormContext({
        organizationId: configData.organizationId,
        formId: configData.formID,
        formName: configData.formName,
        merchantName: configData.nonprofitName,
        formType: FORM_TYPES.STANDARD,
        elementId: elementID,
      });

      // Track form viewed event
      // CRITICAL: Wrap in try-catch to ensure analytics never blocks user flow
      try {
        trackForm(FORM_EVENTS.FORM_VIEWED, {
          form_id: configData.formID,
          form_type: FORM_TYPES.STANDARD,
          form_name: configData.formName,
          merchant_name: configData.nonprofitName,
          is_hosted_page: isHostedPage,
          has_custom_inputs: Boolean(configData.customInputs?.length),
          has_scheduled_donations: showScheduledDonations,
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }
    }
  }, [configData?.formID, elementID, isHostedPage, showScheduledDonations]);

  useEffect(() => {
    let timer;
    if (errorMessage != '') {
      timer = setTimeout(() => {
        setErrorMessage('');
      }, 3000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [errorMessage]);

  useEffect(() => {
    // Resolve the effective amount for a frequency and determine if it's a button selection.
    // When a default amount IS set (non-zero, non-empty), use it directly.
    //   - If it matches a button amount → treat as button selection (custom input stays empty)
    //   - If it doesn't match → treat as custom entry (custom input shows the default)
    // When no default is set, fall back to the first button amount (button selection).
    const resolveAmountAndSource = (defaultAmount, buttonAmounts) => {
      const hasDefault = defaultAmount && defaultAmount !== '0' && defaultAmount !== '';
      if (hasDefault) {
        const matchesButton = buttonAmounts.includes(defaultAmount);
        return { value: defaultAmount, isButton: matchesButton };
      }
      // No default → auto-select first available button amount
      const firstButton = buttonAmounts?.find((amt) => amt && amt !== '');
      return { value: firstButton || '', isButton: true };
    };

    // When URL overrides set an amount, skip the amount reset for any run at the
    // overridden frequency. The ref persists (is NOT cleared on match) so it survives
    // multiple effect runs caused by other deps (amounts arrays) changing across renders.
    // Once the user manually changes frequency, the new frequency won't match, so the
    // skip naturally stops applying.
    const shouldSkipAmountReset = skipAmountResetOnceRef.current || skipAmountResetForFrequencyRef.current === frequency;
    skipAmountResetOnceRef.current = false;

    if (frequency == 'ONE_TIME') {
      setDefaultAmounts(oneTimeAmounts);
      if (!shouldSkipAmountReset) {
        const { value, isButton } = resolveAmountAndSource(defaultOneTimeAmount, oneTimeAmounts);
        setAmount(value);
        setIsButtonSelection(isButton);
      }
      setSelectedFrequency('ONE_TIME');
      setDonationType('Once');
    }
    if (frequency == 'DAILY') {
      setDefaultAmounts(dailyAmounts);
      if (!shouldSkipAmountReset) {
        const { value, isButton } = resolveAmountAndSource(defaultDailyAmount, dailyAmounts);
        setAmount(value);
        setIsButtonSelection(isButton);
      }
      setSelectedFrequency('DAILY');
      setDonationType('Repeat');
    }
    if (frequency == 'WEEKLY') {
      setDefaultAmounts(weeklyAmounts);
      if (!shouldSkipAmountReset) {
        const { value, isButton } = resolveAmountAndSource(defaultWeeklyAmount, weeklyAmounts);
        setAmount(value);
        setIsButtonSelection(isButton);
      }
      setSelectedFrequency('WEEKLY');
      setDonationType('Repeat');
    }
    if (frequency == 'MONTHLY') {
      setDefaultAmounts(monthlyAmounts);
      if (!shouldSkipAmountReset) {
        const { value, isButton } = resolveAmountAndSource(defaultMonthlyAmount, monthlyAmounts);
        setAmount(value);
        setIsButtonSelection(isButton);
      }
      setSelectedFrequency('MONTHLY');
      setDonationType('Repeat');
    }
    if (frequency == 'ANNUALLY') {
      setDefaultAmounts(annuallyAmounts);
      if (!shouldSkipAmountReset) {
        const { value, isButton } = resolveAmountAndSource(defaultAnnuallyAmount, annuallyAmounts);
        setAmount(value);
        setIsButtonSelection(isButton);
      }
      setSelectedFrequency('ANNUALLY');
      setDonationType('Repeat');
    }

    // Clear frequency error when frequency is selected
    if (frequency !== '') {
      setFrequencyError('');
    }

    // Batched with the amount reset above, so a render where this equals `frequency` holds
    // the amount that belongs to it (read by the selection report below).
    setAmountFrequency(frequency);
  }, [
    frequency,
    oneTimeAmounts,
    dailyAmounts,
    weeklyAmounts,
    monthlyAmounts,
    annuallyAmounts,
    defaultOneTimeAmount,
    defaultDailyAmount,
    defaultWeeklyAmount,
    defaultMonthlyAmount,
    defaultAnnuallyAmount,
  ]);

  // A currency change remaps the donor's current selection, never the default.
  useEffect(() => {
    const oldCtx = prevCtxRef.current;
    if (oldCtx.displayCurrency === ctx.displayCurrency && oldCtx.fxRate === ctx.fxRate) return;
    prevCtxRef.current = ctx;
    const data = props.data;
    const bounds = { minUsd: cardMin, maxUsd: maxTransactionAmount };
    const rows = [
      ['ONE_TIME', 'oneTime', setOneTimeAmounts, setDefaultOneTimeAmount],
      ['DAILY', 'daily', setDailyAmounts, setDefaultDailyAmount],
      ['WEEKLY', 'weekly', setWeeklyAmounts, setDefaultWeeklyAmount],
      ['MONTHLY', 'monthly', setMonthlyAmounts, setDefaultMonthlyAmount],
      ['ANNUALLY', 'annually', setAnnuallyAmounts, setDefaultAnnuallyAmount],
    ];
    skipAmountResetOnceRef.current = true;
    for (const [freq, key, setSlots, setDefault] of rows) {
      const usdSlots = data[`${key}Amounts`] || [];
      const slots = displayPresets(usdSlots, ctx, bounds);
      setSlots(slots);
      const defaultKey = `default${key[0].toUpperCase()}${key.slice(1)}Amount`;
      setDefault(displayDefault(data[defaultKey] || '', usdSlots, ctx, bounds));
      if (freq === frequency) {
        const carried = carriedDisplayRef.current;
        if (carried && urlAmountUsdRef.current === carried.usd && carriedDisplayMatches(carried, ctx)) {
          // A carried pick that arrived before this copy's currency resolved (CON-336).
          carriedDisplayRef.current = null;
          if (!carried.usdPinned) urlAmountUsdRef.current = null;
          setAmount(carried.amount);
          setIsButtonSelection(false);
          continue;
        }
        const remapped = remapOnCurrencyChange({
          amount, isButton: isButtonSelection, sourceIndex: presetSourceIndicesRef.current[freq]?.[defaultAmounts.indexOf(amount)],
          pinUsd: urlAmountUsdRef.current,
          newSlots: { slots, sourceIndex: usdSlots.map((_, index) => index) }, oldCtx, newCtx: ctx,
        });
        setAmount(remapped.amount);
        setIsButtonSelection(remapped.isButton);
      }
    }
    // Only a currency/rate change owns this remap; amount edits must not rerun it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.displayCurrency, ctx.fxRate]);

  // URL override effect — apply once after data + frequency are initialized
  // Handles pre-fill from parent page URL params (embedded) or hosted page URL params
  useEffect(() => {
    const { data } = props;
    if (!data) return;
    if (hasAppliedUrlOverridesRef.current) return;
    if (!urlOverrides || Object.keys(urlOverrides).length === 0) return;

    hasAppliedUrlOverridesRef.current = true;
    prefillHoldsStateRef.current = true;

    // Determine the effective frequency: a frequency override this form offers, else the
    // form default (an unknown override is dropped below, so the form stays on its default)
    const freqUpper = urlOverrides.frequency ? urlOverrides.frequency.toUpperCase() : '';
    const frequencyOverrideApplies = Boolean(data.frequencies?.includes(freqUpper));
    const effectiveFrequency = frequencyOverrideApplies ? freqUpper : data.defaultFrequency;

    // Frequency override — apply first so correct amounts are loaded
    if (frequencyOverrideApplies) {
      setFrequency(freqUpper);
      // donationType and selectedFrequency are set by the frequency switching effect above
    }

    // Fund override — case-insensitive match against available funds
    if (urlOverrides.fund && data.funds) {
      const matchedFund = data.funds.find(
        (f) => f.toLowerCase() === urlOverrides.fund.toLowerCase(),
      );
      if (matchedFund) {
        setSelectedFund(matchedFund);
      }
    }

    // Amount override — must be a valid positive number within min/max range.
    // The limits are read from `data`, the same way the data effect above derives the
    // `cardMin` / `maxTransactionAmount` state: this effect runs in the same commit as that
    // one, so the state still holds its placeholders ($1 / $1,000) here (CON-336).
    let amountApplied = false;
    if (urlOverrides.amount) {
      const parsedAmount = parseFloat(urlOverrides.amount);
      const minAmount = data.cardMin ?? 1.0;
      const maxAmount = calculateMaxTransactionAmount(
        data.defaultPaymentProcessor || '',
        data.maxTransactionAmount,
        data.maxTransactionAmountIsProcessorLimit,
      );
      if (!isNaN(parsedAmount) && parsedAmount >= minAmount && parsedAmount <= maxAmount) {
        const amountStr = parsedAmount.toString();
        // Tell the frequency switching effect to skip its amount reset at the effective
        // frequency. We store the target frequency (not a boolean) so the skip survives
        // multiple effect runs caused by other deps (amounts arrays) changing in the same
        // render batch. Only for an amount that applies: a rejected one must leave that
        // reset free to put the form's default on screen.
        skipAmountResetForFrequencyRef.current = effectiveFrequency;
        urlAmountUsdRef.current = amountStr;
        const carried = carriedDisplayFor(urlOverrides.amount);
        if (carriedDisplayMatches(carried, ctx)) {
          if (!carried.usdPinned) urlAmountUsdRef.current = null;
          setAmount(carried.amount);
        } else {
          // Not resolved yet (or another currency): pinned USD for now; the currency change
          // effect puts the carried figure on screen once this copy's currency matches it.
          if (carried) carriedDisplayRef.current = { ...carried, usd: amountStr };
          setAmount(ctx.converted ? displayFixed(amountStr, ctx) : amountStr);
        }
        amountApplied = true;

        // Check if this amount matches a preset button for the effective frequency.
        // Read button amounts from data (not state) since state may not be populated yet.
        const freqAmountsMap = {
          ONE_TIME: data.oneTimeAmounts,
          DAILY: data.dailyAmounts,
          WEEKLY: data.weeklyAmounts,
          MONTHLY: data.monthlyAmounts,
          ANNUALLY: data.annuallyAmounts,
        };
        const buttonAmounts = (freqAmountsMap[effectiveFrequency] || []).map(String);
        const matchesButton = buttonAmounts.includes(amountStr);
        setIsButtonSelection(ctx.converted ? false : matchesButton);
      }
    }
    // CON-336: a carried amount that does not apply here — the donor cleared it, or typed one
    // outside this form's limits in the page's other copy — opens on no amount, never on a
    // default (or a URL amount) the donor did not choose. The carried amount, when there is
    // one, is the `urlOverrides.amount` validated just above (it is merged last).
    if (!amountApplied && typeof props.carriedSelection?.amount === 'string') {
      skipAmountResetForFrequencyRef.current = effectiveFrequency;
      setAmount('');
      setIsButtonSelection(false);
    }

    // Custom input overrides — match ci_* and gci_* URL params to custom input titles
    // Strip non-alphanumeric/underscore chars (e.g., #) so titles like "question #5"
    // match URL params like ci_question_%235 (decoded: ci_question_#5) reliably.
    if (data.customInputs && data.customInputs.length > 0) {
      const ciParams = Object.entries(urlOverrides).filter(([k]) => k.startsWith('ci_') || k.startsWith('gci_'));
      if (ciParams.length > 0) {
        const strip = (s) => s.toLowerCase().replace(/[^a-z0-9_]/g, '');
        const updatedInputs = data.customInputs.map((input) => {
          const normalizedTitle = strip(input.title.replace(/\s+/g, '_'));
          const ciKey = `ci_${normalizedTitle}`;
          const gciKey = `gci_${normalizedTitle}`;
          const match = ciParams.find(([k]) => {
            const nk = strip(k);
            return nk === ciKey || nk === gciKey;
          });
          if (match) {
            // Multi-select dropdowns use comma-separated values in the URL
            const answers = (input.type === 'MULTI_SELECTION_DROPDOWN')
              ? match[1].split(',')
              : [match[1]];
            return { ...input, answers };
          }
          return input;
        });
        setCustomInputsValues(updatedInputs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, urlOverrides]);

  // CON-336: report the donor's amount, frequency and fund in the shape the override effect
  // above reads (amount string, frequency enum, fund name), so the hosted page can open its
  // drawer's copy of this form on them. Only once the configuration has applied and the
  // amount belongs to the frequency on screen — never a placeholder or a half-switched pair.
  // The configured default selection is reported as `null` — there is nothing to carry, so
  // the other copy mounts exactly as it would without this — unless this form had an amount,
  // frequency or fund prefill: then the defaults are a pick the donor moved back to, and must
  // beat that prefill in the other copy too.
  useEffect(() => {
    if (!onSelectionChange || !isInitialized) return;
    if (frequency === '' || amountFrequency !== frequency) return;
    const defaultFrequency = configData.defaultFrequency || '';
    const [defaultAmount, buttonAmounts = []] =
      {
        ONE_TIME: [defaultOneTimeAmount, oneTimeAmounts],
        DAILY: [defaultDailyAmount, dailyAmounts],
        WEEKLY: [defaultWeeklyAmount, weeklyAmounts],
        MONTHLY: [defaultMonthlyAmount, monthlyAmounts],
        ANNUALLY: [defaultAnnuallyAmount, annuallyAmounts],
      }[defaultFrequency] || [];
    // The amount the frequency switching effect resolves for the default frequency.
    const resolvedDefaultAmount =
      defaultAmount && defaultAmount !== '0'
        ? defaultAmount
        : buttonAmounts.find((value) => value && value !== '') || '';
    const hasSelectionPrefill = Boolean(
      urlOverrides?.amount || urlOverrides?.frequency || urlOverrides?.fund,
    );
    const isConfiguredDefault =
      !hasSelectionPrefill &&
      frequency === defaultFrequency &&
      amount === resolvedDefaultAmount &&
      selectedFund === (funds.length >= 1 ? funds[0] : '');
    if (isConfiguredDefault) {
      onSelectionChange(null);
      return;
    }
    if (!ctx.converted || amount === '') {
      onSelectionChange({ amount, frequency, fund: selectedFund });
      return;
    }
    // Under conversion `amount` is a display figure, but the other copy reads a carried amount
    // as USD (it is merged over `?amount=`). Report the USD this pick charges — the pinned URL
    // amount, else the display gift's USD exactly as DONATE derives it — and the figure itself.
    let usdAmount = '';
    try {
      usdAmount =
        urlAmountUsdRef.current ??
        usdStringFromDisplay(normalizeDisplayGift(amount, snapshotOf(ctx)), snapshotOf(ctx));
    } catch {
      // Not a figure this currency can hold: carry no amount rather than a mislabelled one.
      onSelectionChange({ amount: '', frequency, fund: selectedFund });
      return;
    }
    onSelectionChange({
      amount: usdAmount,
      frequency,
      fund: selectedFund,
      display: {
        amount,
        currency: ctx.displayCurrency,
        fxRate: ctx.fxRate,
        usdPinned: urlAmountUsdRef.current != null,
      },
    });
  }, [
    onSelectionChange,
    isInitialized,
    ctx,
    amount,
    frequency,
    amountFrequency,
    selectedFund,
    configData.defaultFrequency,
    funds,
    urlOverrides?.amount,
    urlOverrides?.frequency,
    urlOverrides?.fund,
    oneTimeAmounts,
    dailyAmounts,
    weeklyAmounts,
    monthlyAmounts,
    annuallyAmounts,
    defaultOneTimeAmount,
    defaultDailyAmount,
    defaultWeeklyAmount,
    defaultMonthlyAmount,
    defaultAnnuallyAmount,
  ]);

  useEffect(() => {
    if (errorDisplayTime >= 3) {
      clearInterval(errorMessageInterval);
      setErrorDisplayTime(0);
      setErrorMessage('');
    }
  }, [errorDisplayTime]);

  // Greptile 4315511201: a URL-pinned gift must be bound-checked against the exact USD it
  // pins, not a round trip through its rounded display figure (cardMin 1.06, GBP rate
  // 0.758301 shows £0.80, which converts back to $1.05 and would wrongly reject $1.06).
  // Mirrors the submit-time rule (donateSnapshot branch above): the pin wins while it is
  // still active; once the donor edits the amount the refs above null it out and this
  // falls back to the ordinary display conversion.
  const effectiveUsdAmount = () =>
    urlAmountUsdRef.current != null ? Number(urlAmountUsdRef.current) : usdOf(amount, ctx);

  const hasErrors = () => {
    let hasError = false;

    // Clear previous errors first
    setFrequencyError('');
    setAmountError('');
    setFundError('');
    setScheduledDonationsError('');
    setStartDateError('');
    setEndDateError('');
    setTimeError('');

    if (frequency == '') {
      setFrequencyError('Please select a frequency');
      hasError = true;
    }
    if (amount == '') {
      setAmountError('Please select an amount');
      hasError = true;
    }
    // Check minimum amount — cardMin is per-merchant from prodFeesTable (default $1)
    if (effectiveUsdAmount() < cardMin) {
      setAmountError(ctx.converted ? `Amount must be at least ${formatAmountLabel(boundMessageAmount(cardMin, ctx, 'up'), ctx)}` : `Amount must be at least $${Number(cardMin).toFixed(2)}`);
      hasError = true;
    }
    // Check maximum amount based on payment processor
    if (effectiveUsdAmount() > maxTransactionAmount) {
      const formattedMax = formatWithCommas(Number(maxTransactionAmount).toFixed(2));
      setAmountError(ctx.converted ? `Donations must be less than ${formatAmountLabel(boundMessageAmount(maxTransactionAmount, ctx, 'down'), ctx)}` : `Donations must be less than $${formattedMax}`);
      hasError = true;
    }
    if (selectedFund == '') {
      setFundError('Please select a fund');
      hasError = true;
    }

    // Validate scheduled donations ONLY if user has started filling them in
    // Fields are optional - user can open/close the section without filling
    const {
      startDateError: nextStartDateError,
      endDateError: nextEndDateError,
      timeError: nextTimeError,
    } = getScheduledDonationValidationErrors({
      showScheduledDonations,
      selectedFrequency,
      scheduledDonationsOpen,
      startDate,
      endDate,
      timeValue,
      startDateMessage: 'Please input a date that is today or in the future',
      endDateMessage: 'Please input an end date that is today or in the future',
    });

    if (nextStartDateError || nextEndDateError || nextTimeError) {
      setStartDateError(nextStartDateError);
      setEndDateError(nextEndDateError);
      setTimeError(nextTimeError);
      hasError = true;
    }

    return hasError;
  };

  const shouldShowMonthlyBump = () => {
    return frequency === 'ONE_TIME'
      && parseFloat(amount) > 0
      && configData?.showMonthlyBump === true
      && frequencies.includes('MONTHLY');
  };

  const handleButtonClick = () => {
    const formHasErrors = hasErrors();
    if (formHasErrors) return;

    if (customInputsValues.length > 0) {
      try {
        trackForm(FORM_EVENTS.CUSTOM_INPUTS_VIEWED, {
          form_id: configData?.formID,
          form_type: FORM_TYPES.STANDARD,
          from_step: 'start_page',
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }
      smoothSetPage('CUSTOM_INPUTS_PAGE', 'forward');
      return;
    }

    if (shouldShowMonthlyBump()) {
      smoothSetPage('MONTHLY_BUMP_PAGE', 'forward');
      return;
    }

    handleDonate();
  };

  const handleCustomInputsPageSubmit = () => {
    const customInputsHaveErrors = customInputsRef.current?.hasErrors() || false;
    if (customInputsHaveErrors) return;

    if (shouldShowMonthlyBump()) {
      try {
        trackForm(FORM_EVENTS.CUSTOM_INPUTS_COMPLETED, {
          form_id: configData?.formID,
          form_type: FORM_TYPES.STANDARD,
          next_step: 'monthly_bump',
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }
      smoothSetPage('MONTHLY_BUMP_PAGE', 'forward');
      return;
    }

    try {
      trackForm(FORM_EVENTS.CUSTOM_INPUTS_COMPLETED, {
        form_id: configData?.formID,
        form_type: FORM_TYPES.STANDARD,
        next_step: 'checkout',
      });
    } catch (error) {
      console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
    }
    handleDonate();
  };

  const handleDonate = (override = null) => {
    if (isSubmitting || hostedPagePreview || (!previewMode && props.data?.paymentsEnabled === false)) return;
    if (previewMode && !props.onPreviewSubmit) return;

    // 🔧 FIX: Create deep copy to avoid mutating React state (configData)
    // This prevents cart fields from leaking into regular donation flow
    // and ensures each donation starts with a clean config copy
    const data = stripConfigOnlyKeys(JSON.parse(JSON.stringify(configData)));

    // Monthly bump stays inside the pre-submit drawer flow. If accepted, we pass
    // override { amount, frequency, monthlyBumpAccepted } so the payload uses the bump values
    // without mutating the form beneath the overlay.
    const effectiveFrequency = override?.frequency ?? frequency;
    const effectiveAmount = override?.amount ?? amount;
    const effectiveMonthlyBumpAccepted = override?.monthlyBumpAccepted ?? false;
    let monthlyBumpMetadata = effectiveMonthlyBumpAccepted
      ? buildMonthlyBumpAcceptedMetadata({
          originalAmount: override?.monthlyBumpOriginalAmount ?? amount,
          monthlyAmount: override?.monthlyBumpAmount ?? effectiveAmount,
          originalFrequency: override?.monthlyBumpOriginalFrequency ?? 'ONE_TIME',
        })
      : null;

    // updated configData to include user inputs
    data.frequency = effectiveFrequency;
    data.amount = Number(effectiveAmount);
    data.fund = selectedFund;
    data.customInputsValues = customInputsValues;

    // The USD `amount` is derived in cart mode too: a cart item's amount is its USD charge
    // truth (SPEC §2.3, D-R5). The snapshot is attached only in the DONATE branch, never to
    // UPDATE_CART (§2.2).
    let donateSnapshot = null;
    let displayGiftAmount = null;
    try {
      donateSnapshot = snapshotOf(ctx);
      if (donateSnapshot) {
        displayGiftAmount = normalizeDisplayGift(effectiveAmount, donateSnapshot);
        data.amount =
          urlAmountUsdRef.current == null || effectiveMonthlyBumpAccepted
            ? usdFromDisplay(displayGiftAmount, donateSnapshot)
            : Number(urlAmountUsdRef.current);
        if (effectiveMonthlyBumpAccepted) {
          const originalDisplayAmount = normalizeDisplayGift(
            override?.monthlyBumpOriginalAmount ?? amount,
            donateSnapshot,
          );
          const originalUsd =
            urlAmountUsdRef.current == null
              ? usdFromDisplay(originalDisplayAmount, donateSnapshot)
              : Number(urlAmountUsdRef.current);
          monthlyBumpMetadata = buildMonthlyBumpAcceptedMetadata({
            originalAmount: String(originalUsd),
            monthlyAmount: String(data.amount),
            originalFrequency: override?.monthlyBumpOriginalFrequency ?? 'ONE_TIME',
          });
        }
      }
    } catch (error) {
      setAmountError('Something went wrong. Please refresh and try again.');
      Sentry.captureException(error);
      console.error('[StandardForm] Failed to build DONATE display snapshot:', error);
      return;
    }

    // Add scheduled payments data ONLY for recurring donations (not ONE_TIME)
    // Dual datetime structure: local (for UI) + UTC (for payment API)
    applyScheduledDonationPayloadToData({
      data,
      frequency: effectiveFrequency,
      startDate,
      endDate,
      timeValue,
    });

    normalizeRecurringAmountConfig(data);

    data.hostedPage = hostedPage;
    data.monthlyBumpAccepted = effectiveMonthlyBumpAccepted;
    if (monthlyBumpMetadata) {
      Object.assign(data, applyMonthlyBumpMetadataToDonationData(data, monthlyBumpMetadata));
    }

    data.mappedData = fieldMapper(data.fieldMappings, data.fund, data.customInputsValues, []);
    // Greptile 4315511215: a direct donation (addToCart === false) still becomes an
    // editable cart line below (buildCartItemFromSubmission spreads data.mappedData onto
    // it), so it needs the same display metadata a cart-mode item gets — otherwise a later
    // checkout quantity change falls back to re-converting from USD and drifts off the
    // donor's exact display amount (withCartGift, formatCurrency.js). A no-op for USD/
    // non-converted orgs (attachCartDisplayMetadata returns `data` unchanged when !snap).
    attachCartDisplayMetadata(data, donateSnapshot, displayGiftAmount);

    // NEW: Use standardized color preparation
    const colorData = prepareColorsForPostMessage(brandColors);
    Object.assign(data, colorData);

    attachCartSubmissionMetadata({ data });

    // Interactive previews report the real selection to their local host only.
    // Ordinary editor previews remain inert and never reach this branch.
    if (previewMode) {
      props.onPreviewSubmit({ ...data, cartItems: [buildCartItemFromSubmission({
        data, amount: Number(data.amount),
      })] });
      return;
    }

    // 🛒 CART MODE vs REGULAR DONATION - Critical flow distinction
    if (addToCart === true) {
      // CART MODE: Keep cart fields (cartItems, products) AND ensure nested data object
      // This structure is required for cart checkout routing in external script
      // External script needs data.nonprofitName to determine checkout URL
      if (!data.data || data.data === null) {
        // Create nested data object with critical fields for external script routing
        data.data = {
          nonprofitName: data.nonprofitName,
          elementID: data.elementID,
          formID: data.formID,
          formName: data.formName,
          merchantID: data.merchantID,
          cartItems: data.cartItems || [],
          products: data.products || [],
          monthlyBumpAccepted: effectiveMonthlyBumpAccepted,
        };
      }
      if (typeof data.data === 'object' && data.data !== null) {
        data.data.monthlyBumpAccepted = effectiveMonthlyBumpAccepted;
        if (monthlyBumpMetadata) {
          Object.assign(data, applyMonthlyBumpMetadataToDonationData(data, monthlyBumpMetadata));
        }
      }
    } else {
      // REGULAR DONATION (direct-donate, bypassing AddToCart).
      //
      // Build a single-item cartItems array so the checkout's state.cart is
      // populated and the /checkout/card payload carries a rich funds[] entry.
      // Before this was added, direct-donate submissions arrived at the
      // checkout with cartItems missing, causing DonorInfo.jsx to set
      // state.cart = [] and POST funds=[] to transactions-backend. The
      // backend would then silently fall back to convert_fund_to_funds
      // (card.py:1962) which synthesizes a minimal 10-field fund WITHOUT
      // fundItemId, breaking custom-input-to-fund linkage downstream.
      //
      // This mirrors ProductForm's direct-donate pattern. See
      // buildCartItemFromSubmission in formSubmissionUtils.js for the full
      // shape and rationale.
      data.cartItems = [
        buildCartItemFromSubmission({
          data,
          amount: parseFloat(data.amount),
          quantity: 1,
        }),
      ];
      // Products field isn't used for direct-donate (StandardForm has no
      // product catalog); clear to avoid confusing the external script.
      delete data.products;
      // Only delete data field if it's null/undefined (keep if it has content)
      if (data.data === null || data.data === undefined) {
        delete data.data;
      }
    }

    if (addToCart === true) {
      // Get PostHog session ID for cart analytics continuity
      const posthogSessionId = posthog?.get_session_id?.() || null;

      try {
        trackForm(FORM_EVENTS.CART_ITEM_ADDED, {
          form_id: configData?.formID,
          form_type: FORM_TYPES.STANDARD,
          amount: parseFloat(data.amount) || 0,
          amount_cents: toMinorUnits(data.amount),
          frequency: effectiveFrequency,
          monthly_bump_accepted: effectiveMonthlyBumpAccepted,
          monthly_bump_original_amount_cents: toMinorUnits(monthlyBumpMetadata?.monthlyBumpOriginalAmount),
          monthly_bump_amount_cents: toMinorUnits(monthlyBumpMetadata?.monthlyBumpAmount),
          one_time_to_monthly_conversion: Boolean(effectiveMonthlyBumpAccepted),
          currency: ANALYTICS_CURRENCY,
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }

      safePostMessageWithSelfFallback(window.top, {
        action: 'UPDATE_CART',
        entity: 'CHARITYSTACK',
        sender: 'STANDARD_FORM',
        receiver: 'HOSTED_SCRIPT',
        data: JSON.stringify(data),
        id: data.cartElementID,
        posthogSessionId: posthogSessionId,
      });

      // Reset scheduled donation state after adding to cart
      // This prevents stale dates from bleeding into the next cart item
      clearScheduledDonationState({
        setStartDate,
        setEndDate,
        setTimeValue,
        setScheduledDonationsOpen,
      });

      // Signal any same-document overlay host (OverlayElement) that the form
      // has handed off to the cart sheet, so the overlay can close itself.
      // No-op when this form is rendered as a regular embed or inside a
      // hosted page (no listener mounted in those contexts).
      window.dispatchEvent(new CustomEvent('charitystack:cart-add-triggered'));
    } else {
      // Get PostHog session ID for cross-app analytics continuity
      // Using official PostHog API: get_session_id()
      const posthogSessionId = posthog?.get_session_id?.() || null;

      // Set Sentry flow context for checkout error tracking
      setFlowContext('elements', elementID, {
        amount: data.amount,
        frequency: effectiveFrequency,
      });

      // Add Sentry breadcrumb for donation initiation
      Sentry.addBreadcrumb({
        message: 'User initiated checkout from elements',
        category: 'navigation',
        level: 'info',
        data: {
          form_id: elementID,
          amount: data.amount,
          frequency: effectiveFrequency,
          posthog_session: posthogSessionId,
        },
      });

      // Track form submission before sending to checkout
      // CRITICAL: Wrap in try-catch to ensure analytics never blocks user flow
      try {
        trackForm(FORM_EVENTS.FORM_SUBMITTED, {
          form_id: configData?.formID,
          form_type: FORM_TYPES.STANDARD,
          amount: parseFloat(data.amount) || 0,
          amount_cents: toMinorUnits(data.amount),
          frequency: effectiveFrequency,
          fund: selectedFund,
          is_recurring: effectiveFrequency !== 'ONE_TIME',
          has_scheduled_payment: Boolean(startDate || endDate),
          has_custom_inputs: Boolean(customInputsValues?.length),
          is_hosted_page: hostedPage,
          monthly_bump_accepted: effectiveMonthlyBumpAccepted,
          monthly_bump_original_amount_cents: toMinorUnits(monthlyBumpMetadata?.monthlyBumpOriginalAmount),
          monthly_bump_amount_cents: toMinorUnits(monthlyBumpMetadata?.monthlyBumpAmount),
          one_time_to_monthly_conversion: Boolean(effectiveMonthlyBumpAccepted),
          currency: ANALYTICS_CURRENCY,
        });
      } catch (error) {
        console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
      }

      // Forward URL prefill fields to checkout
      [
        'firstname',
        'lastname',
        'organizationname',
        'email',
        'phone',
        'address',
        'address2',
        'city',
        'state',
        'zip',
        'country',
      ].forEach((f) => {
        if (urlOverrides[f]) data[`prefill_${f}`] = urlOverrides[f];
      });

      // Forward ci_* and gci_* (custom input) params to checkout for global custom inputs
      Object.entries(urlOverrides).forEach(([k, v]) => {
        if (k.startsWith('ci_') || k.startsWith('gci_')) data[k] = v;
      });

      try {
        attachDonateSnapshot(
          data,
          isScheduledDonation(data) ? null : donateSnapshot,
          displayGiftAmount,
        );
      } catch (error) {
        setAmountError('Something went wrong. Please refresh and try again.');
        Sentry.captureException(error);
        console.error('[StandardForm] Failed to attach DONATE display snapshot:', error);
        return;
      }

      setIsSubmitting(true);

      // CON-63 mark 2 — checkout_requested, stamped where the donor pressed the
      // button, on whichever surface this form is rendered by. NOT stamped at the
      // hosted page's `DONATE` re-broadcast, which the plan proposed: that handler
      // returns early whenever the message already carries a `posthogSessionId`,
      // and this form always supplies one from the same PostHog instance — measured,
      // the mark never arrived from there. Capture only; the message is untouched.
      markJourney(JOURNEY_MARKS.CHECKOUT_REQUESTED);

      // CON-86 / PE-2140: the embed script on the host page posts
      // CHECKOUT_PRELOAD_LOST back to THIS iframe when it cannot hand the tap to
      // the checkout. Armed here, beside the DONATE, because that is the only
      // point at which a tap exists to lose. Idempotent — at most one listener
      // per document however many times the donor submits.
      ensureLostCheckoutTapListener({ sender: 'STANDARD_FORM' });

      const postMessagePayload = {
        action: 'DONATE',
        entity: 'CHARITYSTACK',
        sender: 'STANDARD_FORM',
        receiver: 'HOSTED_SCRIPT',
        hostedPage: hostedPage,
        donationData: JSON.stringify(data),
        posthogSessionId: posthogSessionId,
      };

      // DEV ONLY: Log postMessage payload for debugging
      if (isDevelopment()) {
        console.log(
          '📤 [donation-elements/StandardForm] DONATE postMessage payload:',
          postMessagePayload,
        );
        console.log('📤 [donation-elements/StandardForm] Parsed donationData:', data);
      }

      safePostMessageWithSelfFallback(window.top, postMessagePayload);

      // Reset form after standard delay (only for DONATE, not UPDATE_CART)
      const runFormReset = () => {
        pendingFormResetRef.current = null;
        resetFormState();
      };
      pendingFormResetRef.current = {
        run: runFormReset,
        timeoutId: scheduleFormReset(runFormReset, setResetTimeoutId),
      };
    }

    // }, window.location.ancestorOrigins[0]);
  };

  // Functions to clear errors when users interact with fields
  const clearFrequencyError = () => {
    if (frequency !== '') {
      setFrequencyError('');
    }
  };

  const clearAmountError = () => {
    if (amount !== '' && effectiveUsdAmount() >= cardMin && effectiveUsdAmount() <= maxTransactionAmount) {
      setAmountError('');
    }
  };

  const clearFundError = () => {
    if (selectedFund !== '') {
      setFundError('');
    }
  };

  const clearStartDateError = () => {
    if (startDate) {
      const startDateObj = dayjs(startDate).startOf('day');
      const today = dayjs().startOf('day');
      // Clear error if date is today or in the future
      if (startDateObj.isSameOrAfter(today, 'day')) {
        setStartDateError('');
      }
    }
  };

  const clearEndDateError = () => {
    if (endDate) {
      const endDateObj = dayjs(endDate).startOf('day');
      const today = dayjs().startOf('day');
      // Clear error if date is today or in the future
      if (endDateObj.isSameOrAfter(today, 'day')) {
        setEndDateError('');
      }
    }
  };

  const clearTimeError = () => {
    if (timeValue) {
      setTimeError('');
    }
  };

  const openScheduleSheet = (event) => {
    setStartDateError('');
    setEndDateError('');
    setTimeError('');
    try {
      trackForm(FORM_EVENTS.SCHEDULED_DONATIONS_VIEWED, {
        form_id: configData?.formID,
        form_type: FORM_TYPES.STANDARD,
        frequency,
      });
    } catch (error) {
      console.error('[StandardForm] Analytics tracking failed (non-blocking):', error);
    }
    scheduleSheet.open(event);
  };

  const handleScheduledDonationsValidationError = ({
    startDateError: nextStartDateError = '',
    endDateError: nextEndDateError = '',
    timeError: nextTimeError = '',
  }) => {
    setStartDateError(nextStartDateError);
    setEndDateError(nextEndDateError);
    setTimeError(nextTimeError);
  };

  const saveScheduledDonations = ({ startDate: nextStartDate, endDate: nextEndDate, timeValue: nextTimeValue }) => {
    const hasScheduledDates = Boolean(nextStartDate || nextEndDate);

    setStartDate(hasScheduledDates ? nextStartDate : null);
    setEndDate(hasScheduledDates ? nextEndDate : null);
    setTimeValue(hasScheduledDates ? nextTimeValue : null);
    setScheduledDonationsOpen(hasScheduledDates);
    setStartDateError('');
    setEndDateError('');
    setTimeError('');
    scheduleSheet.close();
  };

  const clearScheduledDonations = () => {
    clearScheduledDonationState({
      setStartDate,
      setEndDate,
      setTimeValue,
      setScheduledDonationsOpen,
    });
    setStartDateError('');
    setEndDateError('');
    setTimeError('');
  };

  const fixedFormElementHeight = getStandardizedFormHeight({ formDisplayMode });
  // The drawer keeps its height floor (CON-529 F9 changes only the card): its height lock measures against it.
  const fixedFormElementStyles = getStandardizedFormElementStyles({ formDisplayMode, floor: isDrawerOpen });
  const fixedFormBodyStyles = getStandardizedFormBodyStyles({ formDisplayMode });

  if (!previewMode && configData.active === false) {
    return <FormUnavailable />;
  } else {
    return (
      <div
        ref={standardFormPageContainerRef}
        className={`page-container ${fixedFormElementHeight ? 'form-page-stack' : ''}`}
        data-mode={formDisplayMode === 'overlay' ? 'overlay' : isDrawerOpen ? 'drawer' : 'dynamic'}
        data-name={configData.elementName}
        id={`${elementID}-CS-standard-form`}
        style={{
          minHeight: isDrawerOpen ? '100%' : 'fit-content',
          height: isDrawerOpen ? '100%' : 'auto',
        }}
      >
          <FormStartPageShell
            page={page}
            direction={pageDirection}
            isDrawerOpen={isDrawerOpen}
            isHostedPage={isHostedPage}
            formDisplayMode={formDisplayMode}
            hostedPage={hostedPage}
            primaryColor={primaryColor}
            fixedFormElementHeight={fixedFormElementHeight}
            fixedFormElementStyles={fixedFormElementStyles}
            fixedFormBodyStyles={fixedFormBodyStyles}
            containerId={`${elementID}-CS-standard-form-container`}
            headerId={`${elementID}-CS-standard-form-header`}
            bodyId={`${elementID}-CS-standard-form-body`}
            headerTitle={FORM_TITLE_STRIP}
            onHeaderBack={getDetailPageBack({
              isHostedPage,
              hostedPagePreview,
              enableEmbedForm,
              formDisplayMode,
              setParentPage,
            })}
            drawerBodyClassName="h-full p-0 sm:p-6 rounded-none justify-center"
            nonDrawerBodyClassName=""
            bodyClassName="cs-form-sections"
            faq={null}
            usePageContainer={false}
          >
                  <FormFrequencySection
                    elementID={elementID}
                    containerId={`${elementID}-CS-standard-form-frequencies-container`}
                    containerClassName="w-full"
                    scheduleContainerId={`${elementID}-CS-standard-form-scheduled-donations-container`}
                    frequency={frequency}
                    frequencies={frequencies}
                    selectedFrequency={selectedFrequency}
                    donationType={donationType}
                    setDonationType={setDonationType}
                    onFrequencyChange={handleFrequencyChange}
                    onSelectedFrequencyChange={handleSelectedFrequencyChange}
                    clearFrequencyError={clearFrequencyError}
                    frequencyError={frequencyError}
                    primaryColor={primaryColor}
                    darkColor={darkColor}
                    brandColors={brandColors}
                    isDrawerOpen={isDrawerOpen}
                    formDisplayMode={formDisplayMode}
                    showScheduledDonations={showScheduledDonations}
                    defaultScheduledRange={configData.defaultScheduledRange}
                    scheduledDonationsOpen={scheduledDonationsOpen}
                    setScheduledDonationsOpen={setScheduledDonationsOpen}
                    startDate={startDate}
                    endDate={endDate}
                    timeValue={timeValue}
                    onStartDateChange={(dateValue) => {
                      setStartDate(dateValue);
                      clearStartDateError();
                    }}
                    onEndDateChange={(dateValue) => {
                      setEndDate(dateValue);
                      clearEndDateError();
                    }}
                    onTimeChange={(nextTimeValue) => {
                      setTimeValue(nextTimeValue);
                      clearTimeError();
                    }}
                    onClear={clearScheduledDonations}
                    onEdit={openScheduleSheet}
                    startDateError={startDateError}
                    endDateError={endDateError}
                    timeError={timeError}
                    autoApplyVersion={customInputsResetKey}
                  />
                  <div className="flex flex-col gap-[var(--cs-space-3)]">
                    <div className="w-full">
                      {!FIXED_AMOUNT_ELEMENT_IDS.includes(elementID) && (
                        <div
                          className="pb-0"
                          id={`${elementID}-CS-standard-form-amount-buttons-container`}
                        >
                          <div
                            className="pb-[var(--cs-space-2)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-text-secondary)]"
                          >
                            Amount
                          </div>
                          <StandardAmountButtons
                            primaryColor={primaryColor}
                            amount={amount}
                            setAmount={setAmount}
                            defaultAmounts={defaultAmounts}
                            elementID={elementID}
                            brandColors={brandColors}
                            hasError={amountError !== ''}
                            onAmountChange={clearAmountError}
                            onButtonSelect={() => { urlAmountUsdRef.current = null; setIsButtonSelection(true); }}
                            isButtonSelection={isButtonSelection}
                            isDrawerOpen={isDrawerOpen}
                            formDisplayMode={formDisplayMode}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      className="w-full"
                      id={`${elementID}-CS-standard-form-custom-amount-container`}
                    >
                      <CustomAmountInput
                        amount={amount}
                        setAmount={setAmount}
                        isButtonSelection={isButtonSelection}
                        onCustomAmountTyped={() => { urlAmountUsdRef.current = null; setIsButtonSelection(false); }}
                        maxTransactionAmount={maxTransactionAmount}
                        minTransactionAmount={cardMin}
                        elementID={elementID}
                        brandColors={brandColors}
                        hasError={amountError !== ''}
                        onAmountChange={clearAmountError}
                      />
                      <AnimatedError error={amountError} />
                    </div>
                  </div>
                  {funds.length > 1 && (
                    <div className="w-full" id={`${elementID}-CS-standard-form-funds-container`}>
                      <FundsDropdown
                        funds={funds}
                        selectedFund={selectedFund}
                        setSelectedFund={setSelectedFund}
                        elementID={elementID}
                        brandColors={brandColors}
                        hasError={fundError !== ''}
                        onFundChange={clearFundError}
                        containerClassName="w-full"
                      />
                      <AnimatedError error={fundError} />
                    </div>
                  )}

                  <FormActionButtons
                    paymentsUnavailable={!previewMode && props.data?.paymentsEnabled === false}
                    brandColors={brandColors}
                    primaryColor={primaryColor}
                    elementID={elementID}
                    onClick={handleButtonClick}
                    buttonLabel={getPreCheckoutStepPrimaryButtonLabel({
                      hasCustomInputsStep: customInputsValues.length > 0,
                      hasMonthlyBumpStep: shouldShowMonthlyBump(),
                    })}
                    formType="STANDARD"
                    addToCart={addToCart}
                    frequencies={frequencies}
                    frequency={frequency}
                    amount={amount}
                    isHostedPage={isHostedPage}
                    setParentPage={setParentPage}
                    hostedPagePreview={hostedPagePreview}
                    enableEmbedForm={enableEmbedForm}
                    formDisplayMode={formDisplayMode}
                    isSubmitting={isSubmitting}
                  />
          </FormStartPageShell>

        {customInputsValues.length > 0 && (
          <FormPageShell
            page={page}
            pageName="CUSTOM_INPUTS_PAGE"
            direction={pageDirection}
            isDrawerOpen={isDrawerOpen}
            isHostedPage={isHostedPage}
            formDisplayMode={formDisplayMode}
            primaryColor={primaryColor}
            fixedFormElementHeight={fixedFormElementHeight}
            fixedFormElementStyles={fixedFormElementStyles}
            disableFixedHeightScroll={Boolean(fixedFormElementHeight)}
            usePageContainer={false}
            faq={null}
          >
            <CustomInputsPage
              ref={customInputsRef}
              resetPaginationKey={customInputsResetKey}
              handleDonate={handleCustomInputsPageSubmit}
              setPage={smoothSetPage}
              customInputsValues={customInputsValues}
              setCustomInputsValues={setCustomInputsValues}
              primaryColor={primaryColor}
              hostedPage={hostedPage}
              isDrawerOpen={isDrawerOpen}
              brandColors={brandColors}
              elementID={elementID}
              domID={domID}
              formDisplayMode={formDisplayMode}
              addToCart={addToCart}
              primaryButtonLabel={getPrimaryLabelAfterCustomInputsStep(shouldShowMonthlyBump())}
              checkoutButtonLabel={getFormCheckoutButtonLabel({
                formType: 'STANDARD',
                addToCart,
                amount,
                frequency: getCtaFrequency(frequencies, frequency),
                converted: ctx.converted,
              })}
              enablePagination={Boolean(fixedFormElementHeight)}
            />
          </FormPageShell>
        )}

        {/* Schedule start / end date: a sheet over the form card, not a step. */}
        <ScheduledDonationsSheet
          isActive={scheduleSheet.isOpen}
          container={scheduleSheet.container}
          onCancel={scheduleSheet.close}
          brandColors={brandColors}
          primaryColor={primaryColor}
          selectedFrequency={selectedFrequency}
          startDate={startDate}
          endDate={endDate}
          timeValue={timeValue}
          defaultScheduledRange={configData.defaultScheduledRange}
          startDateError={startDateError}
          endDateError={endDateError}
          timeError={timeError}
          onValidationError={handleScheduledDonationsValidationError}
          onSave={saveScheduledDonations}
        />

        <FormPageShell
          page={page}
          pageName="MONTHLY_BUMP_PAGE"
          direction={pageDirection}
          isDrawerOpen={isDrawerOpen}
          isHostedPage={isHostedPage}
          formDisplayMode={formDisplayMode}
          primaryColor={primaryColor}
          fixedFormElementHeight={fixedFormElementHeight}
          fixedFormElementStyles={fixedFormElementStyles}
          usePageContainer={false}
          faq={null}
        >
          <MonthlyBumpPage
            oneTimeAmount={parseFloat(amount)}
            monthlyBumpAmount={ctx.converted ? displayDefault(String(calculateMonthlyBumpAmount(usdOf(amount, ctx))), [], ctx, { minUsd: cardMin, maxUsd: maxTransactionAmount }) : calculateMonthlyBumpAmount(amount)}
            primaryColor={primaryColor}
            brandColors={brandColors}
            isDrawerOpen={isDrawerOpen}
            isHostedPage={isHostedPage}
            nonprofitName={configData?.nonprofitName}
            formId={configData?.formID}
            formType={FORM_TYPES.STANDARD}
            onAccept={(monthlyAmount) => {
              handleDonate({
                amount: String(monthlyAmount),
                frequency: 'MONTHLY',
                monthlyBumpAccepted: true,
                monthlyBumpOriginalFrequency: 'ONE_TIME',
                monthlyBumpOriginalAmount: amount,
                monthlyBumpAmount: String(monthlyAmount),
              });
            }}
            onDecline={() => {
              handleDonate();
            }}
            onBack={() => {
              smoothSetPage(customInputsValues.length > 0 ? 'CUSTOM_INPUTS_PAGE' : 'START_PAGE', 'back');
            }}
            isSubmitting={isSubmitting}
            elementID={elementID}
            domID={domID}
            formDisplayMode={formDisplayMode}
            bodyStyle={fixedFormBodyStyles}
          />
        </FormPageShell>
      </div>
    );
  }
}

export default StandardForm;
